import React, { useEffect, useState, useMemo } from "react";
import './main.module.css';
import SEOHead from './SEOHead';


export const Header = () => {

  const slides = useMemo(() => [
    {
      image: "/img/img14.webp",
      title: "AI & Automation = Efficiency at Scale",
      paragraph:
        "Transforming processes through AI and automation to deliver scalable, high-efficiency solutions",
    },
    {
      image: "/img/img18.webp",
      title: "Smart Systems + Adaptive Algorithms = Future-Ready Hardware",
      paragraph:
        "Creating hardware solutions that evolve through adaptive algorithms, anticipating and meeting future demands",
    },
    {
      image: "/img/img16.webp",
      title: "Potential >= Passion",
      paragraph:
        "Achieve growth with trusted partners, leveraging tech for a sustainable digital transformation",
    },
    {
      image: "/img/img11.webp",
      title: "Innovation + Sustainability = Endless Impact",
      paragraph: "Innovating with sustainability to create lasting, positive change",
    },
  ], []);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Preload images
  useEffect(() => {
    slides.forEach((slide) => {
      const img = new Image();
      img.src = slide.image;
    });
  }, [slides]);

  // Auto-advance slides
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      
      // After wipe animation completes, update indices
      setTimeout(() => {
        setCurrentIndex(nextIndex);
        setNextIndex((nextIndex + 1) % slides.length);
        setIsTransitioning(false);
      }, 1000); // 1 second wipe animation
      
    }, 3000); // 3.5 seconds between slides

    return () => clearInterval(interval);
  }, [nextIndex, slides.length]);

  return (
    <>
      <SEOHead 
        title="AK Consultants - Innovative Technology Solutions"
        description="Ace Keystone Consultants deliver cutting-edge innovative solutions in AI, geospatial intelligence, biomedical engineering, mobile app development, and smart infrastructure. Transforming ideas into reality since 2022."
      />
      <header id="header">
        <section className="intro">
          <div className="hero-wipe-slider-container">
            {/* Background layer - next image with its text */}
            <div className="hero-wipe-layer hero-wipe-layer-background">
              <div 
                className="hero-wipe-slide"
                style={{ backgroundImage: `url(${slides[nextIndex].image})` }}
              />
              <div className="hero-wipe-overlay"></div>
              <div className="hero-wipe-content-layer">
                <h1>{slides[nextIndex].title}</h1>
                <p>{slides[nextIndex].paragraph}</p>
              </div>
            </div>
            
            {/* Foreground layer - current image with its text (wipes away) */}
            <div className={`hero-wipe-layer hero-wipe-layer-foreground ${isTransitioning ? 'hero-wiping' : ''}`}>
              <div 
                className="hero-wipe-slide"
                style={{ backgroundImage: `url(${slides[currentIndex].image})` }}
              />
              <div className="hero-wipe-overlay"></div>
              <div className="hero-wipe-content-layer">
                <h1>{slides[currentIndex].title}</h1>
                <p>{slides[currentIndex].paragraph}</p>
              </div>
            </div>
          </div>
          
          <style>{`
        /* Hero wipe slider container - scoped to hero section only */
        #header .hero-wipe-slider-container {
          position: relative;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
        }
        
        /* Hero wipe layer - contains image, overlay, and text together */
        #header .hero-wipe-layer {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
        
        /* Background layer - always visible underneath */
        #header .hero-wipe-layer-background {
          z-index: 1;
        }
        
        /* Foreground layer - wipes away to reveal background */
        #header .hero-wipe-layer-foreground {
          z-index: 2;
          clip-path: inset(0 0 0 0);
          transition: none;
        }
        
        /* Wipe animation - right to left reveal */
        #header .hero-wipe-layer-foreground.hero-wiping {
          animation: heroWipeLeft 1s cubic-bezier(0.65, 0, 0.35, 1) forwards;
        }
        
        @keyframes heroWipeLeft {
          0% {
            clip-path: inset(0 0 0 0);
          }
          100% {
            clip-path: inset(0 0 0 100%);
          }
        }
        
        /* Hero wipe slide - fixed background image */
        #header .hero-wipe-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }
        
        /* Hero wipe overlay for text contrast */
        #header .hero-wipe-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(
            circle, 
            rgba(0, 0, 0, 0.6) 0%, 
            rgba(0, 0, 0, 0.4) 40%, 
            rgba(0, 0, 0, 0.2) 80%
          );
          pointer-events: none;
        }
        
        /* Text layer - part of each hero wipe layer */
        #header .hero-wipe-content-layer {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          color: #fff;
          width: 90%;
          max-width: 900px;
          pointer-events: none;
          padding: 0 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        
        #header .hero-wipe-content-layer h1 {
          font-weight: bold;
          margin-bottom: 1rem;
          text-shadow: 2px 2px 20px rgba(0, 0, 0, 0.9);
          font-size: 3rem;
          line-height: 1.2;
          text-align: center !important;
          width: 100%;
          display: block;
          margin-left: 0;
          margin-right: 0;
        }
        
        #header .hero-wipe-content-layer p {
          font-size: 1.5rem;
          line-height: 1.6;
          text-shadow: 1px 1px 10px rgba(0, 0, 0, 0.8);
          max-width: 800px;
          margin: 0 auto;
          text-align: center !important;
          width: 100%;
          display: block;
        }
        
        /* ===== RESPONSIVE MEDIA QUERIES ===== */
        
        /* Extra Large Desktops - 1920px and above */
        @media (min-width: 1920px) {
          #header .hero-wipe-content-layer {
            max-width: 1200px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 5rem !important;
            text-align: center;
            width: 100%;
          }
          #header .hero-wipe-content-layer p {
            font-size: 2rem !important;
            max-width: 1000px;
            text-align: center;
            width: 100%;
          }
        }
        
        /* Large Desktops - 1440px to 1919px - Keep as is (working fine) */
        @media (min-width: 1440px) and (max-width: 1919px) {
          #header .hero-wipe-content-layer {
            max-width: 1100px;
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 4.5rem !important;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.75rem !important;
            max-width: 900px;
          }
        }
        
        /* Standard Desktops - 1200px to 1439px */
        @media (min-width: 1200px) and (max-width: 1439px) {
          #header .hero-wipe-content-layer {
            max-width: 1000px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 4rem !important;
            text-align: center;
            width: 100%;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.5rem !important;
            max-width: 850px;
            text-align: center;
            width: 100%;
          }
        }
        
        /* Small Desktops / Large Laptops - 1025px to 1199px */
        @media (min-width: 1025px) and (max-width: 1199px) {
          #header .hero-wipe-content-layer {
            max-width: 900px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 3.5rem !important;
            text-align: center;
            width: 100%;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.35rem !important;
            max-width: 800px;
            text-align: center;
            width: 100%;
          }
        }
        
        /* Laptops / iPad Pro Landscape - 1024px */
        @media (min-width: 1024px) and (max-width: 1024px) {
          #header .hero-wipe-content-layer {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 3.25rem !important;
            text-align: center !important;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.25rem !important;
            text-align: center !important;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
          }
        }
        
        /* Tablets Landscape / iPad Air, Pro - 820px to 1023px */
        @media (min-width: 820px) and (max-width: 1023px) {
          #header .hero-wipe-content-layer {
            width: 85%;
            max-width: 750px;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 3.5rem !important;
            margin-bottom: 0.85rem;
            text-align: center !important;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.65rem !important;
            line-height: 1.5;
            text-align: center !important;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
          }
        }
        
        /* Tablets Portrait / iPad - 768px to 819px */
        @media (min-width: 768px) and (max-width: 819px) {
          #header .hero-wipe-content-layer {
            width: 85%;
            max-width: 700px;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 3.25rem !important;
            margin-bottom: 0.8rem;
            text-align: center !important;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.5rem !important;
            line-height: 1.5;
            text-align: center !important;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
          }
        }
        
        /* Small Tablets / Large Phones Landscape - 600px to 767px */
        @media (min-width: 600px) and (max-width: 767px) {
          #header .hero-wipe-content-layer {
            width: 90%;
            padding: 0 15px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 3.5rem !important;
            margin-bottom: 0.85rem;
            text-align: center;
          }
          #header .hero-wipe-content-layer p {
            font-size: 2rem !important;
            line-height: 1.6;
            text-align: center;
          }
        }
        
        /* Large Phones - 481px to 599px */
        @media (min-width: 481px) and (max-width: 599px) {
          #header .hero-wipe-content-layer {
            width: 92%;
            padding: 0 12px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 3.25rem !important;
            margin-bottom: 0.8rem;
            line-height: 1.3;
            text-align: center;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.9rem !important;
            line-height: 1.6;
            text-align: center;
          }
        }
        
        /* Standard Phones - 376px to 480px */
        @media (min-width: 376px) and (max-width: 480px) {
          #header .hero-wipe-content-layer {
            width: 94%;
            padding: 0 10px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 3rem !important;
            margin-bottom: 0.75rem;
            line-height: 1.3;
            text-align: center;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.8rem !important;
            line-height: 1.6;
            text-align: center;
          }
        }
        
        /* Small Phones - 320px to 375px */
        @media (min-width: 320px) and (max-width: 375px) {
          #header .hero-wipe-content-layer {
            width: 95%;
            padding: 0 8px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 2.75rem !important;
            margin-bottom: 0.7rem;
            line-height: 1.3;
            text-align: center;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.7rem !important;
            line-height: 1.6;
            text-align: center;
          }
        }
        
        /* Extra Small Phones - below 320px */
        @media (max-width: 319px) {
          #header .hero-wipe-content-layer {
            width: 96%;
            padding: 0 6px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 2.5rem !important;
            margin-bottom: 0.65rem;
            line-height: 1.3;
            text-align: center;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.65rem !important;
            line-height: 1.6;
            text-align: center;
          }
        }
        
        /* Landscape orientation adjustments for mobile devices */
        @media (max-height: 500px) and (orientation: landscape) {
          #header .hero-wipe-content-layer {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          #header .hero-wipe-content-layer h1 {
            font-size: 2.75rem !important;
            margin-bottom: 0.5rem;
            text-align: center;
          }
          #header .hero-wipe-content-layer p {
            font-size: 1.7rem !important;
            line-height: 1.5;
            text-align: center;
          }
        }
        `}</style>
        </section>
      </header>
    </>
  );
};
